import { useState, useEffect } from "react";
import { useApp } from "../../store.jsx";
import { Icon, EmptyState } from "../../components/Common.jsx";
import { listTasks, deleteTask } from "../../lib/api";
import { useSession } from "../../lib/auth-client";
import { AddSelfTaskModal } from "../../components/Modals.jsx";
import AnnouncementBanner from "../../components/announcements/AnnouncementBanner.jsx";

export default function MyTasks() {
  const { toast, openModal, closeModal } = useApp();
  const { data: session } = useSession();
  const userId = session?.user?.id;

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmingId, setConfirmingId] = useState(null);
  const [hoverId, setHoverId] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const { tasks } = await listTasks({ userId });
      setTasks(tasks);
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) refresh();
  }, [userId]); // eslint-disable-line

  const openAddTask = () =>
    openModal(
      <AddSelfTaskModal
        userId={userId}
        onClose={closeModal}
        onSubmit={() => {
          toast("Task added", "success");
          refresh();
        }}
      />,
      "md",
    );

  const removeMine = async (id) => {
    try {
      await deleteTask(id);
      setConfirmingId(null);
      refresh();
    } catch (e) {
      toast(e.message, "error");
    }
  };

  const assigned = tasks.filter((t) => t.source === "assigned");
  const self = tasks.filter((t) => t.source === "self_added");

  // ---- shared styles ----
  const statCard = {
    flex: 1,
    minWidth: 0,
    padding: 14,
    borderRadius: 10,
    border: "1px solid var(--border)",
    background: "var(--surface-2)",
  };
  const statLabel = {
    fontSize: 11.5,
    fontWeight: 600,
    color: "var(--text-muted)",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  };
  const statValue = { fontSize: 22, fontWeight: 700, marginTop: 6 };

  const sectionHeader = (iconName, title, count, accent, action) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "12px 16px",
        borderBottom: "1px solid var(--border)",
        background: "var(--surface-2)",
      }}
    >
      <div
        style={{
          width: 30,
          height: 30,
          borderRadius: 8,
          background: `${accent}22`,
          color: accent,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon name={iconName} size={15} />
      </div>
      <span style={{ fontWeight: 600, fontSize: 14 }}>{title}</span>
      <span className="badge badge-muted" style={{ fontSize: 11 }}>
        {count}
      </span>
      {action && <div style={{ marginLeft: "auto" }}>{action}</div>}
    </div>
  );

  const renderTaskCard = (t, idx, opts = {}) => {
    const { showDelete = false, accent = "var(--border)" } = opts;
    const isHover = hoverId === t.id;
    const isConfirming = confirmingId === t.id;
    return (
      <div
        key={t.id}
        onMouseEnter={() => setHoverId(t.id)}
        onMouseLeave={() => {
          setHoverId(null);
          if (isConfirming) setConfirmingId(null);
        }}
        style={{
          padding: 12,
          paddingLeft: 14,
          borderRadius: 10,
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderLeft: `3px solid ${accent}`,
          display: "flex",
          alignItems: "flex-start",
          gap: 10,
          transition: "border-color .15s ease, background .15s ease",
        }}
      >
        <div
          style={{
            flexShrink: 0,
            minWidth: 28,
            height: 26,
            padding: "0 8px",
            borderRadius: 6,
            background: "var(--surface-2)",
            border: "1px solid var(--border)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 11.5,
            fontWeight: 700,
            color: "var(--text-muted)",
          }}
        >
          Task {idx + 1}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              flexWrap: "wrap",
            }}
          >
            <span style={{ fontWeight: 600, fontSize: 13.5 }}>{t.title}</span>
            {t.source === "assigned" && (
              <span
                className="badge badge-muted"
                style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
              >
                <Icon name="briefcase" size={11} /> from manager
              </span>
            )}
            {t.source === "self_added" && (
              <span className="badge badge-muted">self</span>
            )}
            {(t.projects || []).map((p) => (
              <span
                key={p.id}
                className="badge"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  background: "rgba(99,102,241,0.12)",
                  color: "var(--primary, #6366f1)",
                  border: "1px solid rgba(99,102,241,0.3)",
                }}
                title={`Part of project: ${p.name}`}
              >
                <Icon name="folder" size={11} /> {p.name}
              </span>
            ))}
          </div>
          {t.description && (
            <div
              style={{
                fontSize: 12.5,
                color: "var(--text-muted)",
                marginTop: 6,
                lineHeight: 1.45,
              }}
            >
              {t.description}
            </div>
          )}
        </div>
        {showDelete && (
          <div
            style={{
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {isConfirming ? (
              <>
                <span
                  style={{
                    fontSize: 12,
                    color: "var(--text-muted)",
                    marginRight: 2,
                  }}
                >
                  Delete?
                </span>
                <button
                  type="button"
                  onClick={() => removeMine(t.id)}
                  style={{
                    padding: "4px 10px",
                    borderRadius: 6,
                    border: "1px solid var(--error)",
                    background: "var(--error)",
                    color: "#fff",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmingId(null)}
                  style={{
                    padding: "4px 10px",
                    borderRadius: 6,
                    border: "1px solid var(--border)",
                    background: "transparent",
                    color: "var(--text-muted)",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                className="icon-btn"
                onClick={() => setConfirmingId(t.id)}
                title="Delete"
                style={{
                  opacity: isHover ? 1 : 0.35,
                  transition: "opacity .15s ease",
                }}
              >
                <Icon name="trash" size={14} />
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <section>
      <AnnouncementBanner placement="portal" />
      <div className="page-head">
        <div>
          <h1 className="page-title">My Tasks</h1>
          <p className="page-sub">
            Tasks assigned by your manager, plus any you've added.
          </p>
        </div>
        <button className="btn btn-primary" onClick={openAddTask}>
          <Icon name="plus" size={16} /> Add Task
        </button>
      </div>

      {/* Summary stat strip */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <div style={statCard}>
          <div style={statLabel}>Total tasks</div>
          <div style={statValue}>{tasks.length}</div>
        </div>
        <div style={statCard}>
          <div style={statLabel}>Assigned by manager</div>
          <div style={{ ...statValue, color: "var(--primary, #6366f1)" }}>
            {assigned.length}
          </div>
        </div>
        <div style={statCard}>
          <div style={statLabel}>Added by me</div>
          <div style={{ ...statValue, color: "var(--success, #22c55e)" }}>
            {self.length}
          </div>
        </div>
      </div>

      {/* Assigned section */}
      <div
        className="card"
        style={{
          padding: 0,
          marginBottom: 16,
          overflow: "hidden",
        }}
      >
        {sectionHeader(
          "briefcase",
          "Assigned by manager",
          assigned.length,
          "var(--primary, #6366f1)",
        )}
        <div style={{ padding: 14 }}>
          {loading ? (
            <div
              style={{
                color: "var(--text-muted)",
                fontSize: 13,
                padding: "16px 4px",
                textAlign: "center",
              }}
            >
              Loading…
            </div>
          ) : assigned.length === 0 ? (
            <EmptyState
              title="No assigned tasks yet"
              desc="Your manager hasn't assigned anything yet."
              icon="inbox"
            />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {assigned.map((t, i) =>
                renderTaskCard(t, i, {
                  accent: "var(--primary, #6366f1)",
                }),
              )}
            </div>
          )}
        </div>
      </div>

      {/* My own section */}
      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        {sectionHeader(
          "user",
          "My own tasks",
          self.length,
          "var(--success, #22c55e)",
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={openAddTask}
            style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
          >
            <Icon name="plus" size={14} /> New
          </button>,
        )}

        {/* Self task list */}
        <div style={{ padding: 14 }}>
          {loading ? (
            <div
              style={{
                color: "var(--text-muted)",
                fontSize: 13,
                padding: "16px 4px",
                textAlign: "center",
              }}
            >
              Loading…
            </div>
          ) : self.length === 0 ? (
            <EmptyState
              title="No personal tasks yet"
              desc='Click "Add Task" to track anything you’re working on outside what your manager assigned.'
              icon="plus-circle"
            />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {self.map((t, i) =>
                renderTaskCard(t, i, {
                  showDelete: true,
                  accent: "var(--success, #22c55e)",
                }),
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
